import React, { useState } from "react";
import { resolveTeamLogo as resolveLogo } from "@/lib/sportsAssets";
import { teamThemeColor } from "@/constants/teamThemes";

/* TeamLogo — the small circular team crest used across the live board and the
   event scoreboard (design 02b). It shows a real team logo when we can resolve
   one (NFL/NCAAF/NBA/NCAAB/WNBA/MLB/NHL + top soccer, via an authoritative
   name→logo map baked from ESPN's teams API — see constants/teamLogos.js), and
   otherwise a clean team-coloured monogram chip so every competitor — niche
   leagues, tennis, esports — always renders a defined, on-brand crest. */

// Short abbreviation for the chip. Prefer an explicit ALL-CAPS token from the
// odds label (the moneyline label often starts with the book's abbrev, e.g.
// "TEX Rangers"); fall back to a monogram from the team name.
export function teamAbbr(name = "", labelAbbr = "") {
  const la = String(labelAbbr).trim();
  if (/^[A-Z0-9]{2,4}$/.test(la)) return la;
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "—";
  if (words.length === 1) return words[0].slice(0, 4).toUpperCase();
  // Two/three-word names → first letters (Ohio State -> OSU, Arizona State -> ASU)
  return words.slice(0, 4).map((w) => w[0]).join("").toUpperCase();
}

export default function TeamLogo({ name = "", labelAbbr = "", league = "", sport = "", size = 30, className = "", providerLogoUrl = "" }) {
  const abbr = teamAbbr(name, labelAbbr);
  // Authoritative lookup via the central asset resolver (lib/sportsAssets.js →
  // provider URL → configured map → ESPN). Returns null for leagues/competitors
  // not covered → we render the monogram chip.
  const url = resolveLogo({ league, name, providerLogoUrl: providerLogoUrl || undefined });
  const [broken, setBroken] = useState(false);
  const showImg = url && !broken;
  // authoritative brand colour for known major teams; deterministic fallback otherwise
  const color = teamThemeColor({ team: name, league, sport });
  // Broadcast crest is a rounded SQUARE (design "broadcast-scoreboard"): radius
  // scales with size (~0.26×) — card crest 38px→10px, event hero 72px→~19px.
  const radius = Math.round(size * 0.26);

  if (showImg) {
    return (
      <span
        className={`inline-flex shrink-0 items-center justify-center overflow-hidden bg-[#0C0C0E] ${className}`}
        style={{ width: size, height: size, borderRadius: radius, boxShadow: "inset 0 0 0 1px #27272A" }}
        aria-hidden="true"
      >
        <img
          src={url}
          alt=""
          loading="lazy"
          onError={() => setBroken(true)}
          style={{ width: size * 0.82, height: size * 0.82, objectFit: "contain" }}
        />
      </span>
    );
  }

  // Team-coloured monogram chip (fallback + college/soccer/esports).
  const fs = abbr.length >= 4 ? Math.round(size * 0.3) : Math.round(size * 0.36);
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center font-display font-extrabold uppercase leading-none tracking-tight text-white ${className}`}
      style={{
        width: size, height: size, borderRadius: radius, fontSize: fs, background: color,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.14), inset 0 -6px 12px rgba(0,0,0,0.28)",
      }}
      aria-hidden="true"
    >
      {abbr}
    </span>
  );
}
